/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import React from 'react';
import PropTypes from 'prop-types';
import '../PaymentMethodCard.css';

const PaymentMethodCard = ({
  config,
  isEnabled,
  hasData,
  isConfigured,
  requiresUpgrade,
  actionRequired,
  onEdit,
  onUpgrade,
  loading,
  comingSoon,
  textObj = {}
}) => {
  const t = (key, fallback) => textObj[key] || fallback;

  const getStatusBadge = () => {
    if (loading) {
      return (
        <div className="PaymentMethodCard-statusBadge loading">
          {t("loading", "Loading")}
        </div>
      );
    }

    if (comingSoon) {
      return (
        <div className="PaymentMethodCard-comingSoonBadge">
          {t("comingSoon", "Coming Soon")}
        </div>
      );
    }

    if (requiresUpgrade) {
      const planText = config.requiresPlan === 'pro' ? t("proPlan", "Pro Plan or above") : t("premiumPlan", "Premium Plan only");
      return (
        <div className="PaymentMethodCard-statusBadge premium">
          {planText}
        </div>
      );
    }

    if (actionRequired) {
      return (
        <div className="PaymentMethodCard-statusBadge action-required">
          {t("actionRequired", "Action Required")}
        </div>
      );
    }

    let badgeClass = 'off';
    let badgeLabel = t("off", "Off");

    if (isEnabled) {
      badgeClass = 'active';
      badgeLabel = t("active", "Active");
    } else if (hasData || isConfigured) {
      badgeClass = 'inactive';
      badgeLabel = t("inactive", "Inactive");
    }

    return (
      <div className={`PaymentMethodCard-statusBadge ${badgeClass}`}>
        {badgeLabel}
      </div>
    );
  };

  const cardClassName = `PaymentMethodCard ${comingSoon ? 'coming-soon' : ''}`;

  return (
    <div className={cardClassName}>
      <div className="PaymentMethodCard-logoContainer">
        <div className={`PaymentMethodCard-logo ${config.logoClass}`} />
      </div>

      <div className="PaymentMethodCard-content">
        <div className="PaymentMethodCard-title">{config.label}</div>
        {getStatusBadge()}
      </div>

      <div className="PaymentMethodCard-actions">
        {requiresUpgrade && !comingSoon ? (
          <button
            className="PaymentMethodCard-upgradeBtn"
            onClick={onUpgrade}
            disabled={loading}
          >
            {t("upgrade", "Upgrade")}
          </button>
        ) : (
          <button
            className="PaymentMethodCard-editBtn"
            onClick={() => onEdit(config)}
            disabled={loading || comingSoon}
          >
            {t("edit", "Edit")}
          </button>
        )}
      </div>
    </div>
  );
};

PaymentMethodCard.propTypes = {
  config: PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    logoClass: PropTypes.string.isRequired,
    requiresPlan: PropTypes.string
  }).isRequired,
  isEnabled: PropTypes.bool,
  hasData: PropTypes.bool,
  isConfigured: PropTypes.bool,
  requiresUpgrade: PropTypes.bool,
  actionRequired: PropTypes.bool,
  onEdit: PropTypes.func.isRequired,
  onUpgrade: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  comingSoon: PropTypes.bool,
  textObj: PropTypes.object
};

PaymentMethodCard.defaultProps = {
  isEnabled: false,
  hasData: false,
  isConfigured: false,
  requiresUpgrade: false,
  actionRequired: false,
  loading: false,
  comingSoon: false
};

export default PaymentMethodCard;
